import axios from 'axios'
import NProgress from 'nprogress'
import { notification } from 'antd'
import * as Sentry from '@sentry/react'
import { push } from 'connected-react-router'
import { isArrayBuffer } from 'lodash-es'
import { matchPath } from 'react-router'
import { setMaintenance } from 'app-redux/maintenance/actions'
import { setLoadedInit } from 'app-redux/inits/actions'
import { closeLoginForm, setErrorLoginForm, showLoginForm } from 'app-redux/loginSlice'
import { showNoInternetModal } from 'app-redux/networkSlice'
import { tokenExpired } from 'app-redux/user/actions'
import { decodeUriWithAtob, generateBaseUri } from './helper'
import { refreshToken as refreshTokenApi } from './apis/user'
import {
  clearAuthTokens,
  getExpiresAt,
  getOrCreateDeviceId,
  getRefreshToken,
  getToken,
  setExpiresAt,
  setRefreshExpiresAt,
  setRefreshToken,
  setToken,
} from './cookies'
import { teardownPushNotification } from './pushNotification'

const customAxios = axios.create({
  baseURL: generateBaseUri(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000,
  withCredentials: true,
})

/**
 * Event `offline` / `online` — dipakai membedakan "benar offline" dari CORS saat masih online.
 * Axios untuk net::ERR_INTERNET_DISCONNECTED hampir selalu mengirim ERR_NETWORK + "Network Error"
 * (sama seperti CORS), jadi tidak bisa diandalkan tanpa sinyal ini.
 */
let isBrowserOffline =
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? !navigator.onLine
    : false

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    isBrowserOffline = true
  })
  window.addEventListener('online', () => {
    isBrowserOffline = false
  })
}

/**
 * Helper: Cek apakah request memerlukan Authorization header
 */
const isAuthorization = (config = {}) => {
  return !!config.headers?.Authorization
}

/**
 * Hanya untuk benar-benar tidak ada koneksi / terputus dari jaringan.
 * Jangan pakai ERR_NETWORK + "Network Error" sendirian — itu juga dipakai CORS saat online.
 *
 * Respons HTTP dari BE (termasuk status error: 4xx, 5xx seperti 500) selalu punya
 * `error.response` di Axios → selalu false di sini, sehingga modal no-internet tidak muncul.
 */
export const isNetworkDisconnectedError = (error) => {
  if (error?.response != null) {
    return false
  }

  const message = String(error?.message || '').toLowerCase()
  const explicitDisconnect =
    message.includes('err_internet_disconnected') ||
    message.includes('internet disconnected') ||
    message.includes('net::err_internet_disconnected')

  if (explicitDisconnect) {
    return true
  }

  const navOffline =
    typeof navigator !== 'undefined' &&
    Object.prototype.hasOwnProperty.call(navigator, 'onLine') &&
    navigator.onLine === false

  if (navOffline) {
    return true
  }

  const looksLikeAxiosTransportFailure =
    error?.code === 'ERR_NETWORK' || message.includes('network error')

  if (isBrowserOffline && looksLikeAxiosTransportFailure) {
    return true
  }

  return false
}

// ============== JWT REFRESH TOKEN MECHANISM ==============
// Refresh dijalankan saat request kena 401, atau proaktif saat token hampir expired dan
// tab Kledo sedang fokus (startFocusRefresh). Tab background tidak pernah refresh sendiri.

// Single-flight refresh per tab: semua 401 yang bersamaan + ensureFreshToken menunggu promise
// yang sama, supaya tidak ada dua request /refresh berbarengan
let refreshPromise = null

// Nama Web Lock untuk refresh lintas tab, supaya beberapa tab yang kena 401 bersamaan
// cukup memicu satu request /refresh (kalau BE me-rotate refresh token, mencegah logout).
const REFRESH_LOCK_NAME = 'kledo-auth-refresh'

/**
 * Jalankan refresh token dengan single-flight (per tab) + Web Lock (lintas tab).
 * Token/refresh_token/expires_at hasil response disimpan oleh successHandler,
 * jadi di sini cukup menunggu request-nya selesai.
 */
const runRefresh = () => {
  if (refreshPromise) {
    return refreshPromise
  }

  const usedRefreshToken = getRefreshToken()
  const usedAccessToken = getToken()
  if (!usedRefreshToken) {
    return Promise.reject(new Error('No refresh token'))
  }

  const doRefresh = async () => {
    const latestRefreshToken = getRefreshToken()
    // Tab lain sudah logout selama menunggu lock
    if (!latestRefreshToken) {
      throw new Error('No refresh token')
    }
    // Tab lain sudah refresh selama menunggu lock → pakai token hasilnya. Access token ikut
    // dicek karena BE tidak selalu me-rotate refresh token.
    if (latestRefreshToken !== usedRefreshToken || getToken() !== usedAccessToken) {
      return
    }
    await refreshTokenApi({ refresh_token: latestRefreshToken })
  }

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  const request = locks?.request ? locks.request(REFRESH_LOCK_NAME, doRefresh) : doRefresh()

  refreshPromise = request.finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

/**
 * Parse expires_at string ke timestamp (milliseconds)
 * Format: "2026-02-06 19:36:52" atau ISO format
 */
const parseExpiresAt = (expiresAtString) => {
  if (!expiresAtString) {
    return null
  }
  try {
    // Coba parse sebagai ISO format dulu
    const isoDate = new Date(expiresAtString)
    if (!Number.isNaN(isoDate.getTime())) {
      return isoDate.getTime()
    }
    // Jika bukan ISO, coba parse format "YYYY-MM-DD HH:mm:ss"
    const [datePart, timePart] = expiresAtString.split(' ')
    if (datePart && timePart) {
      const [year, month, day] = datePart.split('-')
      const [hour, minute, second] = timePart.split(':')
      const date = new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10),
        parseInt(second, 10),
      )
      return date.getTime()
    }
    return null
  } catch {
    return null
  }
}

const FRESH_TOKEN_BUFFER = 60 * 1000 // 60 detik
const FOCUS_REFRESH_INTERVAL = 30 * 1000 // 30 detik

/**
 * Waktu expired access token (milliseconds).
 * Utamakan claim `exp` JWT (epoch UTC) — `expires_at` dari BE tanpa zona waktu dan diparse
 * sebagai waktu lokal browser, jadi meleset untuk user di luar WIB.
 * @returns {number|null}
 */
const getTokenExpiryMs = () => {
  const payload = getToken()?.split('.')[1]
  if (payload) {
    try {
      const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
      if (exp) {
        return exp * 1000
      }
    } catch {
      // Bukan JWT → fallback ke expires_at
    }
  }
  return parseExpiresAt(getExpiresAt())
}

const isTokenNearExpiry = () => {
  const expiryMs = getTokenExpiryMs()
  return !!expiryMs && expiryMs - Date.now() <= FRESH_TOKEN_BUFFER
}

/**
 * Pastikan access token siap pakai sebelum dipakai di luar axios interceptor
 * (mis. handoff token ke aplikasi eksternal lewat URL).
 * Refresh hanya dilakukan jika token sudah / hampir expired (< 60 detik).
 * @returns {Promise<string|null>} token terbaru, atau null jika sesi sudah tidak valid
 */
export const ensureFreshToken = async () => {
  const currentToken = getToken()
  if (!currentToken) {
    return null
  }

  // Token masih jauh dari expired, atau sesi non-JWT (tidak ada refresh_token / expires_at)
  if (!getRefreshToken() || !isTokenNearExpiry()) {
    return currentToken
  }

  try {
    // Single-flight: kalau interceptor 401 sedang refresh, ikut hasilnya
    await runRefresh()
  } catch (error) {
    // Refresh gagal. Jika 401, errorHandler sudah clear auth + tampilkan login,
    // sehingga getToken() di bawah mengembalikan undefined -> caller batal jalan.
    console.warn('Ensure fresh token failed:', error)
  }

  return getToken() || null
}

// Dimatikan jika jam perangkat meleset (token baru tetap terlihat expired) → cukup jalur 401
let isFocusRefreshDisabled = false

/**
 * Refresh proaktif hanya saat tab Kledo terlihat & fokus, dan token hampir expired.
 * Klik pertama user tidak perlu menunggu 401 → refresh → retry.
 */
const refreshIfNearExpiry = async () => {
  if (
    isFocusRefreshDisabled ||
    document.visibilityState !== 'visible' ||
    !document.hasFocus() ||
    !getRefreshToken() ||
    !isTokenNearExpiry()
  ) {
    return
  }

  try {
    await runRefresh()
    if (isTokenNearExpiry()) {
      isFocusRefreshDisabled = true
    }
  } catch (error) {
    // Gagal di sini tidak fatal — request berikutnya tetap ditangani jalur 401
    console.warn('Focus refresh failed:', error)
  }
}

/**
 * Pasang pemicu refresh proaktif: saat tab kembali fokus/terlihat, dan cek berkala selama fokus.
 * Dipanggil sekali saat aplikasi dimulai (main.jsx).
 */
export const startFocusRefresh = () => {
  window.addEventListener('focus', refreshIfNearExpiry)
  document.addEventListener('visibilitychange', refreshIfNearExpiry)
  setInterval(refreshIfNearExpiry, FOCUS_REFRESH_INTERVAL)
}

const redirectPath = ({ history, pathname }) => {
  const isTwoFactorRequired = pathname.includes('/user/login/two-factor-required')
  const twoFactorRequiredData = matchPath(pathname, {
    path: '/user/login/two-factor-required/:data',
    exact: true,
  })?.params?.data
  const twoFactorRequiredDecondedData = decodeUriWithAtob(twoFactorRequiredData, false)

  return isTwoFactorRequired && twoFactorRequiredDecondedData?.source
    ? twoFactorRequiredDecondedData?.source
    : pathname.startsWith('/shopify-claim')
      ? `/shopify-claim${history?.location?.search}`
      : '/user/login'
}

/**
 * Clear auth dan redirect ke login saat refresh gagal atau token tidak valid
 * @param {object} authError - Axios error (opsional); jika 401 dengan body { message }, ditampilkan di LoginModal Alert
 */
const clearAuthAndRedirect = (store, history, getState, authError) => {
  clearAuthTokens()
  NProgress.done()
  const pathname = history?.location?.pathname || ''
  if (pathname !== '/user/login') {
    NProgress.done()

    const state = getState()
    const hasSelectedCompany = !!state?.companies?.website_id
    const backendMessage = authError?.response?.data?.message

    if (hasSelectedCompany) {
      store.dispatch(
        showLoginForm({
          message: backendMessage ? '' : 'Sesi Anda telah berakhir, silakan login kembali.',
        }),
      )
      store.dispatch(setErrorLoginForm(backendMessage || null))
    } else {
      // API logout tidak bisa dipanggil (sesi sudah habis), jadi putus token
      // push di sisi FCM agar perangkat ini berhenti menerima push akun ini.
      // Cabang modal login di atas sengaja tidak: bila yang login ulang akun
      // lain, token otomatis didaftarkan ulang ke akun tsb.
      teardownPushNotification()
      store.dispatch(tokenExpired())
      store.dispatch(closeLoginForm())
      store.dispatch(push(redirectPath({ history, pathname })))
    }
  }
}

const openNoInternetModal = (store) => {
  store.dispatch(closeLoginForm())
  store.dispatch(showNoInternetModal())
}

/**
 * Request interceptor: Otomatis menyisipkan access_token ke Authorization header
 * SELALU ambil token terbaru dari cookie untuk memastikan token selalu fresh
 * (Cookie selalu di-update saat refresh berhasil)
 */
export const requestHandler = (request) => {
  if (isAuthorization(request)) {
    // SELALU ambil token terbaru dari cookie untuk memastikan token fresh
    // Cookie selalu di-update saat refresh berhasil, jadi ini memastikan request pakai token terbaru
    const latestToken = getToken()
    if (latestToken) {
      request.headers.Authorization = `Bearer ${latestToken}`
    }
  } else {
    delete request.headers.Authorization
    const token = getOrCreateDeviceId()
    request.headers['X-Device-Token'] = token
    request.headers['X-Device-Agent'] = navigator.userAgent
  }

  return request
}

/**
 * Response interceptor - Error handler dengan JWT refresh logic
 * Menangani 401 (token expired) dengan mekanisme:
 * - Stale token: token sudah diperbarui request/tab lain → retry tanpa refresh
 * - runRefresh: single-flight per tab + Web Lock lintas tab
 * - Retry logic: _retry flag mencegah infinite loop
 */
export const errorHandler = (error, store, history) => {
  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  const originalRequest = error.config
  const reduxStore = store?.store || store
  const { getState } = reduxStore

  const status = error.response?.status

  // singleLogin dipakai untuk login awal DAN verifikasi OTP. 401 di sini artinya
  // kredensial / kode OTP salah-kadaluarsa, BUKAN sesi expired. Jangan refresh /
  // clearAuth / redirect — biarkan user tetap di halaman; handler lokal tiap
  // pemanggil (mutation onError / catch LoginModal) yang menampilkan pesan error.
  if (status === 401 && originalRequest?.url?.includes('/authentication/singleLogin')) {
    return Promise.reject(error.response ?? error)
  }

  // 401 selalu alur refresh / clearAuthAndRedirect (modal login), bukan modal no-internet
  if (status !== 401 && isNetworkDisconnectedError(error)) {
    // Request latar belakang (sync Mode Zero-Loading) opt-out: gagal jaringan ditangani
    // antreannya sendiri (retry otomatis), kasir tidak perlu diinterupsi modal.
    if (!originalRequest?.silentOffline) {
      openNoInternetModal(reduxStore)
    }
    return Promise.reject(error.response ?? error)
  }

  // Request dari endpoint refresh yang gagal
  // Cek berdasarkan URL atau flag _isRefreshRequest
  const isRefreshRequest =
    originalRequest?._isRefreshRequest ||
    originalRequest?.url?.includes('/authentication/refresh') ||
    originalRequest?.url?.includes('/refresh')
  if (isRefreshRequest) {
    // 429 Too Many Requests = rate limit, bukan token invalid → jangan clear auth / modal login
    if (status === 429) {
      return Promise.reject(error.response ?? error)
    }
    if (status === 401) {
      clearAuthAndRedirect(reduxStore, history, getState, error)
    }
    return Promise.reject(error.response ?? error)
  }

  // Retry logic: Request sudah pernah di-retry, hindari infinite loop
  if (originalRequest?._retry) {
    if (status === 401) {
      clearAuthAndRedirect(reduxStore, history, getState, error)
    }
    return Promise.reject(error.response ?? error)
  }

  if (status === 401) {
    const refreshToken = getRefreshToken()

    // Tidak ada refresh_token (misal: user belum pakai JWT atau session lama)
    if (!refreshToken) {
      clearAuthAndRedirect(reduxStore, history, getState, error)
      return Promise.reject(error.response ?? error)
    }

    // Token sudah diperbarui request / tab lain setelah request ini dikirim → cukup retry dengan
    // token terbaru, tanpa refresh. Tidak set _retry: kalau masih 401, tetap boleh lewat jalur
    // refresh (bukan langsung logout). _staleTokenRetry mencegah shortcut ini berulang.
    const latestToken = getToken()
    if (
      isAuthorization(originalRequest) &&
      !originalRequest._staleTokenRetry &&
      latestToken &&
      originalRequest.headers.Authorization !== `Bearer ${latestToken}`
    ) {
      originalRequest._staleTokenRetry = true
      return axios(originalRequest)
    }

    // Jalankan refresh (single-flight + lintas tab), lalu retry. Semua 401 yang bersamaan
    // menunggu refresh yang sama. _retry: kalau retry masih 401, sesi memang invalid → logout.
    originalRequest._retry = true

    return runRefresh()
      .then(() => axios(originalRequest))
      .catch((refreshError) => {
        // errorHandler me-reject refresh yang gagal dengan error.response (punya .status)
        const refreshStatus = refreshError?.status ?? refreshError?.response?.status
        // 429 pada refresh = rate limit, bukan token expired → jangan clear auth
        if (refreshStatus === 429) {
          const rateLimitMsg =
            refreshError?.data?.message ||
            refreshError?.response?.data?.message ||
            'Terlalu banyak permintaan. Silakan coba lagi dalam beberapa saat.'
          notification.warning({ description: rateLimitMsg, key: 'refresh-429' })
          return Promise.reject(refreshError?.response ?? refreshError)
        }
        if (refreshStatus !== 401 && isNetworkDisconnectedError(refreshError)) {
          openNoInternetModal(reduxStore)
          return Promise.reject(refreshError?.response ?? refreshError)
        }
        // 401 dari /refresh sudah di-clearAuth oleh cabang isRefreshRequest. Refresh token hilang
        // (tab lain logout selama menunggu lock) juga berarti sesi habis.
        if (refreshStatus === 401 || !getRefreshToken()) {
          clearAuthAndRedirect(reduxStore, history, getState, refreshError)
        }
        return Promise.reject(refreshError?.response ?? refreshError)
      })
  }

  // Handle status lain (bukan 401)
  let errorMessage
  try {
    if (isArrayBuffer(error?.response?.data)) {
      errorMessage = JSON.parse(new TextDecoder().decode(error?.response?.data)).message
    } else {
      errorMessage = error?.response?.data?.message || 'Network error'
    }
  } catch {
    errorMessage = error?.response?.data?.message || 'Network error'
  }

  switch (status) {
    case 521:
      NProgress.done()
      reduxStore.dispatch(setMaintenance(errorMessage))
      reduxStore.dispatch(setLoadedInit())
      reduxStore.dispatch(push('/maintenance'))
      break
    case 422:
      Sentry.captureException(errorMessage)
      break
    default:
      break
  }

  const isSilencedMessage =
    errorMessage === 'Network error' ||
    errorMessage === 'Unauthenticated' ||
    errorMessage === 'Unauthenticated.'

  // Opt-out per-request. Dipakai query yang di-refetch massal (mis. calculate forecast harga
  // di tiap baris item): satu perubahan bulan me-refetch N baris, jadi toast global berarti
  // N notifikasi sekaligus. Pemanggil yang opt-out wajib menampilkan errornya sendiri inline.
  const isErrorNotificationSkipped = !!originalRequest?.skipErrorNotification

  if (!isSilencedMessage && !isErrorNotificationSkipped) {
    notification.warning({
      description: errorMessage,
      key: errorMessage,
    })
  }

  return Promise.reject(error.response ?? error)
}

/**
 * Response interceptor - Success handler
 * Simpan access_token, refresh_token, dan expires_at dari response login/refresh
 */
export const successHandler = (response) => {
  const data = response.data?.data ?? response.data
  const nestedData = response.data?.data?.data ?? data
  const newAccessToken =
    data?.access_token ?? nestedData?.access_token ?? response.data?.access_token
  const newRefreshToken =
    data?.refresh_token ?? nestedData?.refresh_token ?? response.data?.refresh_token
  const expiresAt = data?.expires_at ?? nestedData?.expires_at ?? response.data?.expires_at
  const refreshExpiresAt =
    data?.refresh_expires_at ?? nestedData?.refresh_expires_at ?? response.data?.refresh_expires_at

  if (newAccessToken && newAccessToken !== getToken()) {
    setToken(newAccessToken)
  }
  if (newRefreshToken) {
    setRefreshToken(newRefreshToken)
  }
  if (refreshExpiresAt) {
    setRefreshExpiresAt(refreshExpiresAt)
  }
  if (expiresAt) {
    setExpiresAt(expiresAt)
  }

  return response
}

// Create custom axios GET with cancellation
// Menggunakan axios default (bukan customAxios) agar konsisten dengan semua service lain
export const makeGetRequestCreator = () => {
  let call
  return (url, config = {}) => {
    if (call) {
      call.cancel('Only one request allowed at a time.')
    }
    call = axios.CancelToken.source()
    return axios.get(url, {
      ...config,
      cancelToken: call.token,
    })
  }
}

export const getOnce = makeGetRequestCreator()

// Re-export token helpers untuk kemudahan penggunaan
// eslint-disable-next-line import/export
export {
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
  getExpiresAt,
  setExpiresAt,
  getRefreshExpiresAt,
  setRefreshExpiresAt,
  clearAuthTokens,
} from './cookies'

/**
 * CONTOH PENGGUNAAN:
 *
 * 1. LOGIN (singleLogin dengan use_jwt=true):
 *    import { singleLogin } from 'utils/apis'
 *    const res = await singleLogin(email, password, remember)
 *    // successHandler otomatis menyimpan access_token & refresh_token
 *
 * 2. REFRESH (otomatis via interceptor saat 401):
 *    // Tidak perlu panggil manual - interceptor handle otomatis
 *    // Manual (opsional): import { refreshToken } from 'utils/apis'
 *    const res = await refreshToken({ refresh_token: getRefreshToken() })
 *
 * 3. LOGOUT:
 *    import { logout } from 'app-redux/user/actions'
 *    dispatch(logout())  // Panggil API + clearAuthTokens
 *    // Atau clear lokal saja: import { clearAuthTokens } from 'utils/axios'
 *    clearAuthTokens()
 */
// customAxios tidak lagi digunakan - semua request menggunakan axios default
// Tetap dipertahankan untuk backward compatibility jika ada yang masih import
export default customAxios
